import React, { useCallback, useEffect, useState } from 'react';
import { BiTimeFive } from 'react-icons/bi';
import { FaWaze } from 'react-icons/fa';
import { BsExclamationCircle } from 'react-icons/bs';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { HiPrinter } from 'react-icons/hi';
import { IOrder } from '../../../../interfaces/IOrder';

import * as S from './styles';
import BRLMoneyFormat from '../../../../utils/BRLMoneyFormat';
import { useToast } from '../../../../hooks/toast';
import { useMonitor } from '../../../../hooks/newMonitor';
import Internship from '../../Internship';
import OrdersService from '../../../../services/OrdersService';
import { useAuth } from '../../../../hooks/auth';
import { IDeliveryAddress } from '../../../../interfaces/IDeliveryAddress';
import { IPaymentMethod } from '../../../../interfaces/IPaymentMethod';
import { IOrderItem } from '../../../../interfaces/IOrderItem';
import { distanceBetweenDatesInFull } from '../../../../utils/DistanceBetweenDatesInFull';
import { IIfoodOrder } from '../../../../interfaces/IIfoodOrder';

import IfoodService from '../../../../services/IfoodService';
import Load from '../../../../newComponents/Load';
import { CancelOrderModal } from './CancelOrder';
import CustomPrinterIfood from '../../CustomPrinterIfood';

interface ContentProps {
  selectedOrderId: string;
  setIsLoadingMain: (value: boolean) => void;
}

export function getPaymentMethod(method: string) {
  switch (method) {
    case 'CASH':
      return 'Dinheiro';
    case 'CREDIT':
      return 'Cartão de Crédito';
    case 'DEBIT':
      return 'Débito';
    case 'MEAL_VOUCHER':
      return 'Vale Refeição';
    case 'FOOD_VOUCHER':
      return 'Vale Comida';
    case 'GIFT_CARD':
      return 'Cartão Presente';
    case 'DIGITAL_WALLET':
      return 'Carteira Digital';
    case 'PIX':
      return 'Pix';
    case 'OTHER':
      return 'Outro';
    default:
      return '';
  }
}

export function ContentIfood({
  selectedOrderId,
  setIsLoadingMain,
}: ContentProps) {
  const history = useHistory();

  const [order, setOrder] = useState<
    | (Partial<IOrder> & {
        deliveryAddress?: Partial<IDeliveryAddress>;
        orderPayment: Partial<IPaymentMethod> & { companyPaymentMethod?: any }; // alterar
        items: Partial<IOrderItem>[];
      })
    | null
  >(null);
  const [isLoading, setIsLoading] = useState<'aceitar' | 'cancel' | null>(null);
  const [ifoodOrder, setIfoodOrder] = useState<IIfoodOrder | null>(null);
  const [openCancelOrderModal, setOpenCancelOrderModal] = useState(false);
  const [change, setChange] = useState(0);

  const { addToast } = useToast();

  const { listOrders, setListOrders, filter, checkEmAguardo } = useMonitor();
  const { user, caixa } = useAuth();

  const redirectToWaze = (): void => {
    if (ifoodOrder) {
      const linkWaze = `https://waze.com/ul?ll=${ifoodOrder.delivery.deliveryAddress.coordinates.latitude},${ifoodOrder.delivery.deliveryAddress.coordinates.longitude}&z=10`;
      window.open(linkWaze);
    } else {
      addToast({
        title: 'Pedido não possui endereço de  entrega.',
        type: 'info',
      });
    }
  };

  const loadPedidoData = useCallback(async () => {
    if (!selectedOrderId || !user.empresa_id) return;
    IfoodService.getOrder(selectedOrderId, user.empresa_id).then((res) => {
      setIfoodOrder(res);
      setIsLoadingMain(false);
    });
  }, [order, user.empresa_id, selectedOrderId]);

  const handleSetPedidoConfirmado = async (value: boolean): Promise<void> => {
    if (!ifoodOrder) return;

    try {
      if (value) {
        setIsLoading('aceitar');

        await IfoodService.updateStatus(
          ifoodOrder._id,
          user.empresa_id,
          'CONFIRMED',
        );

        addToast({
          title: 'Pedido Confirmado',
          type: 'success',
        });
      } else {
        setIsLoading('cancel');

        handleOpenCancelOrder();
      }

      OrdersService.listOrders(
        user.empresa_id,
        caixa.id,
        listOrders?.currentPage || 1,
        20,
        filter.insternship.join(','),
        filter.text,
      ).then((res) => {
        return setListOrders(res);
      });

      loadPedidoData();
    } catch (err) {
      const { message } = (err as any).response.data;

      addToast({
        title: 'Erro',
        description: `${message}`,
        type: 'error',
      });
    }
    setIsLoading(null);
  };

  const handleSetPedidoEmAguardo = async (value: boolean): Promise<void> => {
    if (!ifoodOrder) return;

    try {
      if (value) {
        setIsLoading('aceitar');

        await IfoodService.updateStatus(
          ifoodOrder._id,
          user.empresa_id,
          'PREPARATION_STARTED',
        );

        addToast({
          title: 'Pedido em Preparo',
          type: 'success',
        });
      } else {
        setIsLoading('cancel');

        handleOpenCancelOrder();
      }

      OrdersService.listOrders(
        user.empresa_id,
        caixa.id,
        listOrders?.currentPage || 1,
        20,
        filter.insternship.join(','),
        filter.text,
      ).then((res) => {
        return setListOrders(res);
      });

      loadPedidoData();
    } catch (err) {
      const { message } = (err as any).response.data;

      addToast({
        title: 'Erro',
        description: `${message}`,
        type: 'error',
      });
    }
    setIsLoading(null);
  };

  const handleSetPedidoAceito = async (value: boolean): Promise<void> => {
    if (!ifoodOrder) return;

    try {
      if (value) {
        setIsLoading('aceitar');

        await IfoodService.updateStatus(
          ifoodOrder._id,
          user.empresa_id,
          'DISPATCHED',
        );

        addToast({
          title: 'Pedido Expedido',
          type: 'success',
        });
      } else {
        setIsLoading('cancel');

        handleOpenCancelOrder();
      }

      OrdersService.listOrders(
        user.empresa_id,
        caixa.id,
        listOrders?.currentPage || 1,
        20,
        filter.insternship.join(','),
        filter.text,
      ).then((res) => {
        return setListOrders(res);
      });

      loadPedidoData();
    } catch (err) {
      const { message } = (err as any).response.data;

      addToast({
        title: 'Erro',
        description: message,
        type: 'error',
      });
    }
    setIsLoading(null);
  };

  const handleWithdraw = async (value: boolean): Promise<void> => {
    if (!ifoodOrder) return;

    try {
      if (value) {
        setIsLoading('aceitar');

        await IfoodService.updateStatus(
          ifoodOrder._id,
          user.empresa_id,
          'READ_TO_PICKUP',
        );

        addToast({
          title: 'Pedido pronto para a retirada',
          type: 'success',
        });
      } else {
        setIsLoading('cancel');

        handleOpenCancelOrder();
      }

      OrdersService.listOrders(
        user.empresa_id,
        caixa.id,
        listOrders?.currentPage || 1,
        20,
        filter.insternship.join(','),
        filter.text,
      ).then((res) => {
        return setListOrders(res);
      });

      loadPedidoData();
    } catch (err) {
      const { message } = (err as any).response.data;

      addToast({
        title: 'Erro',
        description: message,
        type: 'error',
      });
    }
    setIsLoading(null);
  };

  useEffect(() => {
    checkEmAguardo();

    if (ifoodOrder) {
      const updatedOrderFromList = listOrders?.orders.find(
        (order) => order.ifoodOrder?._id === ifoodOrder._id,
      );

      if (
        updatedOrderFromList &&
        updatedOrderFromList.ifoodOrder?.status !== ifoodOrder.status
      ) {
        loadPedidoData();
      }
    }
  }, [filter.insternship, listOrders, checkEmAguardo]);

  useEffect(() => {
    if (selectedOrderId) loadPedidoData();
    else if (order) setOrder(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadPedidoData, selectedOrderId]);

  useEffect(() => {
    if (ifoodOrder) {
      const changeFor = ifoodOrder.payments.methods.find(
        (method) => method.method === 'CASH',
      )?.cash.changeFor;
      const value = ifoodOrder.payments.methods.find(
        (method) => method.method === 'CASH',
      )?.value;

      if (changeFor && value) {
        setChange(Number(changeFor) - value);
      }
    }
  }, [ifoodOrder]);

  const getCompletedHours = (string: string): string => {
    const newDate = new Date(string);

    return `${(newDate.getHours() < 10 ? '0' : '') + newDate.getHours()}:${
      (newDate.getMinutes() < 10 ? '0' : '') + newDate.getMinutes()
    }`;
  };

  function getCorrespondingStatusNumber(status: string) {
    switch (status) {
      case 'PLACED':
        return 0;
      case 'CONFIRMED':
        return 1;
      case 'CANCELLED':
        return 2;
      case 'PREPARATION_STARTED':
        return 3;
      case 'DISPATCHED':
        return 5;
      case 'CONCLUDED':
        return 6;
      case 'READ_TO_PICKUP':
        return 11;
      default:
        return 0;
    }
  }

  function getTranslatedSponsorship(name: string) {
    switch (name) {
      case 'IFOOD':
        return 'Ifood';
      case 'MERCHANT':
        return 'Loja';
      case 'EXTERNAL':
        return 'Externo';
      default:
        return '';
    }
  }

  const handleOpenCancelOrder = (): void => {
    setOpenCancelOrderModal(true);
  };

  const handleCloseCancelOrder = (): void => {
    setOpenCancelOrderModal(false);
  };

  return ifoodOrder ? (
    <S.ContainerInfo>
      <div className="header">
        <div className="header__title">
          <div>
            <h1>
              Pedido <span>{`#${ifoodOrder.displayId}`}</span>
            </h1>
            <div className="header__title__functions a1">
              <CustomPrinterIfood ifoodOrder={ifoodOrder}>
                <HiPrinter size={20} />
              </CustomPrinterIfood>
            </div>
          </div>

          <p>
            {`Criado ${distanceBetweenDatesInFull(
              ifoodOrder.createdAt,
            )}, às ${getCompletedHours(ifoodOrder.createdAt)} por ${
              ifoodOrder.customer.name ?? 'Venda balcão'
            }`}
          </p>

          <div className="header__title__functions a2">
            <CustomPrinterIfood ifoodOrder={ifoodOrder}>
              <HiPrinter size={20} />
              <p>Imprimir</p>
            </CustomPrinterIfood>
          </div>
        </div>

        <div className="header__buttons">
          {ifoodOrder.status === 'PLACED' && (
            <>
              <button
                disabled={!!isLoading}
                type="button"
                onClick={() => handleSetPedidoConfirmado(true)}
                style={{
                  backgroundColor: '#00c853',
                  color: '#fff',
                }}
              >
                Confirmar
              </button>

              <button
                disabled={!!isLoading}
                type="button"
                onClick={() => handleSetPedidoConfirmado(false)}
              >
                {isLoading === 'cancel' ? (
                  <div className="loader" />
                ) : (
                  'Rejeitar'
                )}
              </button>
            </>
          )}

          {ifoodOrder.status === 'CONFIRMED' && (
            <>
              <button
                disabled={!!isLoading}
                type="button"
                onClick={() => handleSetPedidoEmAguardo(true)}
                style={{
                  backgroundColor: '#00c853',
                  color: '#fff',
                }}
              >
                Iniciar Preparo
              </button>

              <button
                disabled={!!isLoading}
                type="button"
                onClick={() => handleSetPedidoEmAguardo(false)}
              >
                {isLoading === 'cancel' ? (
                  <div className="loader" />
                ) : (
                  'Rejeitar'
                )}
              </button>
            </>
          )}

          {ifoodOrder.status === 'PREPARATION_STARTED' &&
            ifoodOrder.orderType === 'DELIVERY' && (
              <>
                <button
                  disabled={!!isLoading}
                  type="button"
                  onClick={() => handleSetPedidoAceito(true)}
                >
                  {isLoading === 'aceitar' ? (
                    <div className="loader" />
                  ) : (
                    'Expedir'
                  )}
                </button>
                <button
                  disabled={!!isLoading}
                  type="button"
                  onClick={() => handleSetPedidoAceito(false)}
                >
                  {isLoading === 'cancel' ? (
                    <div className="loader" />
                  ) : (
                    'Cancelar'
                  )}
                </button>
              </>
            )}

          {ifoodOrder.status === 'PREPARATION_STARTED' &&
            ifoodOrder.orderType === 'TAKEOUT' && (
              <>
                <button
                  disabled={!!isLoading}
                  type="button"
                  onClick={() => handleWithdraw(true)}
                >
                  {isLoading === 'aceitar' ? (
                    <div className="loader" />
                  ) : (
                    'Pronto retirada'
                  )}
                </button>
                <button
                  disabled={!!isLoading}
                  type="button"
                  onClick={() => handleWithdraw(false)}
                >
                  {isLoading === 'cancel' ? (
                    <div className="loader" />
                  ) : (
                    'Cancelar'
                  )}
                </button>
              </>
            )}
        </div>
      </div>

      <S.Section>
        <div className="section__header">
          <h2>Acompanhe o pedido</h2>
        </div>

        <div className="section__content">
          <Internship
            estagio={getCorrespondingStatusNumber(ifoodOrder.status)}
            order={
              {
                createdAt: ifoodOrder.createdAt,
                startedAt: '',
                preparationStartAt: ifoodOrder.preparationStartDateTime,
                endedAt: '',
                readyToTookAt: '',
                deliveryStartedAt: '',
              } as IOrder
            }
          />
        </div>
      </S.Section>

      {ifoodOrder.schedule ? (
        <S.Section className="programada">
          <BiTimeFive size={20} />
          <h2>
            Entrega programada para:{' '}
            <span>
              {moment(
                new Date(ifoodOrder.schedule.deliveryDateTimeStart),
              ).format('LLLL')}
            </span>
          </h2>
        </S.Section>
      ) : null}

      {ifoodOrder.orderType === 'TAKEOUT' ? (
        <S.Section className="retirada">
          <BsExclamationCircle size={20} />
          <h2>Retirada balcão</h2>
        </S.Section>
      ) : null}

      <div className="order__info">
        <S.Section>
          <div className="section__header">
            <h2>Cliente</h2>
          </div>

          <div className="section__info">
            <div>
              <p>Nome</p>
              <p>
                {ifoodOrder.customer
                  ? ifoodOrder.customer.name
                  : 'Venda balcão'}
              </p>
            </div>
            {ifoodOrder.customer?.phone?.number && (
              <div>
                <p>Telefone</p>
                <p>{ifoodOrder.customer.phone.number}</p>
              </div>
            )}
            {ifoodOrder.customer?.phone?.localizer && (
              <div>
                <p>Ifood ID</p>
                <p>{ifoodOrder.customer.phone.localizer}</p>
              </div>
            )}
            {ifoodOrder.delivery && ifoodOrder.delivery.pickupCode && (
              <div>
                <p>Código de Coleta</p>
                <p>{ifoodOrder.delivery.pickupCode}</p>
              </div>
            )}
            {ifoodOrder.customer.documentNumber && (
              <div>
                <p>Documento</p>
                <p>{ifoodOrder.customer.documentNumber}</p>
              </div>
            )}
          </div>
        </S.Section>

        <S.Section>
          <div className="section__header">
            <h2>Pagamento</h2>
          </div>

          <div className="section__info">
            <div>
              <p>Pago</p>
              <p>
                {ifoodOrder.payments.prepaid.toLocaleString(
                  'pt-BR',
                  BRLMoneyFormat,
                )}
              </p>
            </div>
            <div>
              <p>Pendente</p>
              <p>
                {ifoodOrder.payments.pending.toLocaleString(
                  'pt-BR',
                  BRLMoneyFormat,
                )}
              </p>
            </div>
            {ifoodOrder.payments.methods.map((method) => (
              <div key={method._id}>
                <p>{getPaymentMethod(method.method)}</p>
                {method.method === 'CREDIT' ? (
                  <p>{method.card.brand}</p>
                ) : (
                  <p>{method.prepaid ? 'Pago' : 'Pendente'}</p>
                )}
                <p>{method.value.toLocaleString('pt-BR', BRLMoneyFormat)}</p>
              </div>
            ))}

            <div>
              {!!ifoodOrder.payments.methods.find(
                (method) => method.method === 'CASH',
              ) &&
                change > 0 && (
                  <>
                    <p>Troco</p>
                    <p>{change.toLocaleString('pt-BR', BRLMoneyFormat)}</p>
                  </>
                )}
            </div>
          </div>
        </S.Section>

        {ifoodOrder.delivery?.observations && (
          <S.Section>
            <div className="section__header">
              <h2>Observações</h2>
            </div>

            <div className="section__info">
              <div>
                <p>{ifoodOrder.delivery.observations}</p>
              </div>
            </div>
          </S.Section>
        )}

        {ifoodOrder.benefits && ifoodOrder.benefits.length > 0 && (
          <S.Section>
            <div className="section__header">
              <h2>Promoções</h2>
            </div>

            <div className="section__info">
              {ifoodOrder.benefits.map((benefit) => (
                <React.Fragment key={benefit.targetId}>
                  <div>
                    <p>{benefit.target}</p>
                    <p>
                      {Number(benefit.value).toLocaleString(
                        'pt-BR',
                        BRLMoneyFormat,
                      )}
                    </p>
                  </div>
                  {benefit.sponsorshipValues.map(
                    (value) =>
                      value.value > 0 && (
                        <div>
                          <p style={{ marginLeft: '10px' }}>
                            {getTranslatedSponsorship(value.name)}
                          </p>
                          <p>
                            {Number(value.value).toLocaleString(
                              'pt-BR',
                              BRLMoneyFormat,
                            )}
                          </p>
                        </div>
                      ),
                  )}
                </React.Fragment>
              ))}
            </div>
          </S.Section>
        )}
      </div>

      {ifoodOrder.orderType === 'DELIVERY' && (
        <div className="address">
          <S.Section>
            <div className="section__header">
              <h2>Endereço</h2>
            </div>

            <div className="section__info">
              <div>
                <p>Logradouro</p>
                <p>{ifoodOrder.delivery.deliveryAddress.streetName}</p>
              </div>
              <div>
                <p>Bairro</p>
                <p>{ifoodOrder.delivery.deliveryAddress.neighborhood}</p>
              </div>
              <div>
                <p>Estado</p>
                <p>{ifoodOrder.delivery.deliveryAddress.state}</p>
              </div>
              <div>
                <p>Cidade</p>
                <p>{ifoodOrder.delivery.deliveryAddress.city}</p>
              </div>
              <div>
                <p>Número</p>
                <p>{ifoodOrder.delivery.deliveryAddress.streetNumber}</p>
              </div>
              <div>
                <p>Complemento</p>
                <p>{ifoodOrder.delivery.deliveryAddress.complement}</p>
              </div>
            </div>
          </S.Section>
          <S.Section>
            <div className="section__header">
              <h2>Rota de Entrega</h2>
              <div
                className="section__header__icon waze"
                data-tooltip="Abrir link do waze"
                onClick={redirectToWaze}
              >
                <FaWaze size={16} />
              </div>
            </div>

            <iframe
              title="waze"
              src={`https://embed.waze.com/iframe?zoom=6&lat=${ifoodOrder.delivery.deliveryAddress.coordinates.latitude}lon=${ifoodOrder.delivery.deliveryAddress.coordinates.longitude}&pin=1`}
              width="300"
              height="400"
            />
          </S.Section>
        </div>
      )}

      <S.Section>
        <div className="section__header">
          <h2>Itens do pedido</h2>
        </div>
        <div className="section__itens">
          {ifoodOrder.items.map((item, index, array) => (
            <React.Fragment key={item.id}>
              <div className="order__item">
                <div className="order__item__description">
                  <span>{Number(item.quantity)}</span>
                  {/* eslint-disable-next-line no-nested-ternary */}
                  <p>{item.name}</p>
                  <p>
                    ({Number(item.price).toLocaleString(
                      'pt-BR',
                      BRLMoneyFormat,
                    )})
                  </p>
                </div>
                <p>
                  {Number(item.totalPrice).toLocaleString(
                    'pt-BR',
                    BRLMoneyFormat,
                  )}
                </p>
              </div>

              {!!item.observations && (
                <div className="note">
                  <p>Observação:</p>
                  <p>{item.observations}</p>
                </div>
              )}

              {item.options.map((complemento) => (
                <div className="order__item__complement" key={complemento.id}>
                  <div>
                    <p>{Number(complemento.quantity)}</p>
                    <p>{complemento.name}</p>
                  </div>
                  <hr />
                  <p>
                    {Number(complemento.price).toLocaleString(
                      'pt-BR',
                      BRLMoneyFormat,
                    )}
                  </p>
                </div>
              ))}

              {index < array.length - 1 && <S.DashedDivider />}
            </React.Fragment>
          ))}
        </div>

        <div className="section__total">
          <div>
            <p>Total dos itens:</p>
            <p>
              {ifoodOrder.total.subTotal.toLocaleString(
                'pt-BR',
                BRLMoneyFormat,
              )}
            </p>
          </div>
          {ifoodOrder.orderType === 'DELIVERY' && (
            <div>
              <p>Taxa de entrega:</p>
              <p>
                {ifoodOrder.total.deliveryFee.toLocaleString(
                  'pt-BR',
                  BRLMoneyFormat,
                )}
              </p>
            </div>
          )}
          {ifoodOrder.total.benefits > 0 && (
            <div>
              <p>Desconto:</p>
              <p>
                {ifoodOrder.total.benefits.toLocaleString(
                  'pt-BR',
                  BRLMoneyFormat,
                )}
              </p>
            </div>
          )}
          {ifoodOrder.total.additionalFees > 0 && (
            <div>
              <p>Custos adicionais:</p>
              <p>
                {ifoodOrder.total.additionalFees.toLocaleString(
                  'pt-BR',
                  BRLMoneyFormat,
                )}
              </p>
            </div>
          )}
          <div>
            <p>Valor total:</p>
            <p>
              {ifoodOrder.total.orderAmount.toLocaleString(
                'pt-BR',
                BRLMoneyFormat,
              )}
            </p>
          </div>
        </div>
      </S.Section>
      {openCancelOrderModal && (
        <CancelOrderModal
          companyId={user.empresa_id}
          loadPedidoData={loadPedidoData}
          handleClose={handleCloseCancelOrder}
          open={openCancelOrderModal}
          orderId={ifoodOrder._id}
        />
      )}
    </S.ContainerInfo>
  ) : (
    <div
      style={{
        display: 'flex',
        flex: '1',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Load />
    </div>
  );
}
